import {useMutation} from '@apollo/client';
import {DeleteRawTransactionsDocument} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import RawTransactionList from 'src/RawTransactionList';
import {evictFeePeriodQueries} from 'src/UnlinkBenefitTransactionModal';
import type {BasicRawTransactionFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type DeleteRawTransactionModalUserProps = {
  onDelete: (deleted: string[]) => void;
  rawTransactions: BasicRawTransactionFragment[];
};

export const DeleteRawTransactionModal = (
  props: DeleteRawTransactionModalUserProps & SystemProps,
) => {
  const [deleteRawTransaction, {loading}] = useMutation(
    DeleteRawTransactionsDocument,
    {
      // The delete clears benefit links server-side (changing benefits'
      // computed usage), and a deleted row may also back a fee period as its
      // charge — which the fragment cannot see. Drop cached fee period
      // queries unconditionally, the same way link/unlink do.
      update: evictFeePeriodQueries,
    },
  );
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = () => {
    const ids = props.rawTransactions.map((rt) => rt._id);
    setError(null);
    deleteRawTransaction({
      variables: {ids},
    })
      .then(() => {
        props.onRequestClose();
        props.onDelete(ids);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to delete.');
      });
  };

  const footer = (
    <>
      <Button variant="outline" onClick={props.onRequestClose}>
        Cancel
      </Button>
      <Button variant="destructive" onClick={handleDelete} disabled={loading}>
        Delete
      </Button>
    </>
  );
  return (
    <Modal
      header="Confirm delete"
      onRequestClose={props.onRequestClose}
      footer={footer}
    >
      <RawTransactionList transactions={props.rawTransactions} />
      {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
    </Modal>
  );
};
